import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Dropdown, Spinner, Alert, Tooltip, OverlayTrigger 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';

export default function Devis() {
    const navigate = useNavigate();
    const [devis, setDevis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_proforma', direction: 'desc' });

    useEffect(() => {
        fetchDevis();
        fetchStats();
    }, [filtreStatut]);

    const fetchDevis = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/devis.php';
            
            if (filtreStatut !== 'tous') {
                url += `?statut=${filtreStatut}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setDevis(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/devis.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: 'secondary', icon: 'ri-draft-line', label: 'Brouillon' },
            envoye: { color: 'primary', icon: 'ri-send-plane-line', label: 'Envoyé' },
            accepte: { color: 'success', icon: 'ri-check-double-line', label: 'Accepté' },
            refuse: { color: 'danger', icon: 'ri-close-circle-line', label: 'Refusé' },
            converti_facture: { color: 'info', icon: 'ri-file-list-3-line', label: 'Converti' },
            expire: { color: 'warning', icon: 'ri-time-line', label: 'Expiré' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <Badge 
                bg={config.color} 
                className="px-3 py-2 statut-badge"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.color}), var(--bs-${config.color}-dark))`,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const estExpire = (dateValidite) => {
        return new Date(dateValidite) < new Date();
    };

    const filteredDevis = useMemo(() => {
        return devis
            .filter(d => 
                d.numero_proforma?.toLowerCase().includes(filterText.toLowerCase()) ||
                d.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                d.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_proforma') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_proforma) - new Date(b.date_proforma)
                        : new Date(b.date_proforma) - new Date(a.date_proforma);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc'
                        ? a.net_a_payer - b.net_a_payer
                        : b.net_a_payer - a.net_a_payer;
                }
                return 0;
            });
    }, [devis, filterText, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="loading-container">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement des devis...</p>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (error) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button 
                            variant="outline-danger" 
                            className="mt-3"
                            onClick={fetchDevis}
                        >
                            <i className="ri-refresh-line me-2"></i>
                            Réessayer
                        </Button>
                    </Alert>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon bg-primary-soft">
                                        <i className="ri-file-copy-line text-primary"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Gestion des devis</h1>
                                        <p className="page-subtitle mb-0">
                                            Créez et suivez vos devis et propositions commerciales
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/ventes/devis/nouveau')}
                                    className="btn-gradient px-4 py-2"
                                >
                                    <i className="ri-add-line me-2"></i>
                                    Nouveau devis
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                   {stats && (
    <Row className="g-3 mb-4 stats-row">
        <Col sm={6} lg={3}>
            <Card className="stat-card border-0 shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div 
                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                            style={{ 
                                width: '56px', 
                                height: '56px', 
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                boxShadow: '0 8px 16px rgba(67, 97, 238, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <i className="ri-file-copy-line text-white" style={{ fontSize: '28px', lineHeight: 1 }}></i>
                        </div>
                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">Devis (mois)</span>
                            <h2 className="stat-value text-primary mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                {stats.mois?.nb_devis || 0}
                            </h2>
                            <small className="stat-trend text-primary d-flex align-items-center gap-1 mt-1">
                                <i className="ri-arrow-up-line"></i>
                                <span className="text-truncate">{formatCurrency(stats.mois?.total_montant || 0)}</span>
                            </small>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>

        <Col sm={6} lg={3}>
            <Card className="stat-card border-0 shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div 
                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                            style={{ 
                                width: '56px', 
                                height: '56px', 
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                boxShadow: '0 8px 16px rgba(16, 185, 129, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <i className="ri-check-double-line text-white" style={{ fontSize: '28px', lineHeight: 1 }}></i>
                        </div>
                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">Acceptés</span>
                            <h2 className="stat-value text-success mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                {stats.statuts?.find(s => s.statut === 'accepte')?.nb || 0}
                            </h2>
                            <small className="stat-trend text-success d-flex align-items-center gap-1 mt-1">
                                <i className="ri-arrow-up-line"></i>
                                <span className="text-truncate">{formatCurrency(stats.statuts?.find(s => s.statut === 'accepte')?.total || 0)}</span>
                            </small>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>

        <Col sm={6} lg={3}>
            <Card className="stat-card border-0 shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div 
                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                            style={{ 
                                width: '56px', 
                                height: '56px', 
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                boxShadow: '0 8px 16px rgba(245, 158, 11, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <i className="ri-time-line text-white" style={{ fontSize: '28px', lineHeight: 1 }}></i>
                        </div>
                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">Expirés</span>
                            <h2 className="stat-value text-warning mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                {stats.expires?.nb || 0}
                            </h2>
                            <small className="stat-trend text-warning d-flex align-items-center gap-1 mt-1">
                                <i className="ri-alert-line"></i>
                                <span>À relancer</span>
                            </small>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>

        <Col sm={6} lg={3}>
            <Card className="stat-card border-0 shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div 
                            className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                            style={{ 
                                width: '56px', 
                                height: '56px', 
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                boxShadow: '0 8px 16px rgba(59, 130, 246, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <i className="ri-bar-chart-2-line text-white" style={{ fontSize: '28px', lineHeight: 1 }}></i>
                        </div>
                        <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                            <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">Conversion</span>
                            <h2 className="stat-value text-info mb-0 fw-bold" style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                                {stats.conversion?.total > 0 
                                    ? Math.round((stats.conversion?.convertis || 0) / stats.conversion?.total * 100) 
                                    : 0}%
                            </h2>
                            <small className="stat-trend text-info d-flex align-items-center gap-1 mt-1">
                                <i className="ri-arrow-up-line"></i>
                                <span>{stats.conversion?.convertis || 0} convertis</span>
                            </small>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    </Row>
)}

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={4}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher devis, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="tous">📋 Tous les statuts</option>
                                        <option value="brouillon">📝 Brouillons</option>
                                        <option value="envoye">📨 Envoyés</option>
                                        <option value="accepte">✅ Acceptés</option>
                                        <option value="refuse">❌ Refusés</option>
                                        <option value="converti_facture">📄 Convertis</option>
                                    </Form.Select>
                                </Col>
                                <Col md={5} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count">{filteredDevis.length}</span>
                                        <span className="text-muted ms-2">devis trouvé(s)</span>
                                    </div>
                                    <Button
                                        variant="link"
                                        className="text-muted ms-3"
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <i className={`ri-equalizer-line fs-5 ${showFilters ? 'text-primary' : ''}`}></i>
                                    </Button>
                                </Col>
                            </Row>
                            
                            {showFilters && (
                                <Row className="mt-3">
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Trier par date</option>
                                            <option>Plus récents</option>
                                            <option>Plus anciens</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Filtrer par client</option>
                                            <option>Clients actifs</option>
                                            <option>Nouveaux clients</option>
                                        </Form.Select>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Liste des devis */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead>
                                        <tr>
                                            <th>N° Devis</th>
                                            <SortableHeader field="date_proforma">Date</SortableHeader>
                                            <th>Client</th>
                                            <th>Validité</th>
                                            <SortableHeader field="montant">Montant TTC</SortableHeader>
                                            <th>Statut</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDevis.length > 0 ? (
                                            filteredDevis.map((devis) => {
                                                const expire = estExpire(devis.date_validite);
                                                const statutFinal = expire && devis.statut === 'envoye' ? 'expire' : devis.statut;
                                                
                                                return (
                                                    <tr key={devis.id_proforma} className="facture-row">
                                                        <td>
                                                            <Link 
                                                                to={`/ventes/devis/${devis.id_proforma}`} 
                                                                className="facture-link"
                                                                title={devis.numero_proforma}
                                                            >
                                                                <span className="facture-num">
                                                                    {devis.numero_proforma?.length > 8 
                                                                        ? devis.numero_proforma.substring(0, 8) + '...' 
                                                                        : devis.numero_proforma}
                                                                </span>
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <span className="facture-date">
                                                                {formatDate(devis.date_proforma)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="client-info">
                                                                <span className="client-name fw-bold">{devis.client_nom}</span>
                                                                <p className="client-code" style={{fontSize:'10px'}}>{devis.code_client}</p>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="echeance-info">
                                                                <span className={`echeance-date ${expire ? 'text-danger fw-bold' : ''}`}>
                                                                    {formatDate(devis.date_validite)}
                                                                </span>
                                                                {expire && devis.statut === 'envoye' && (
                                                                    <Badge bg="warning" className="ms-2 retard-badge">
                                                                        Expiré
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="text-end" style={{minWidth:'150px'}}>
                                                            <span className="montant-ttc fw-bold text-primary">
                                                                {formatCurrency(devis.net_a_payer)}
                                                            </span>
                                                        </td>
                                                        <td>{getStatutBadge(statutFinal)}</td>
                                                      <td className="text-center">
    <div className="actions-container">
        {/* Bouton Voir - toujours visible */}
        <ActionButton
            variant="outline-info"
            icon="ri-eye-line"
            onClick={() => navigate(`/ventes/DetailDevis/${devis.id_proforma}`)}
            tooltip="Voir détails"
        />
        
        {/* Bouton Modifier/Statut - visible pour brouillon ET envoyé */}
        {(devis.statut === 'brouillon' || devis.statut === 'envoye') && (
            <ActionButton
                variant={devis.statut === 'brouillon' ? 'outline-primary' : 'outline-warning'}
                icon={devis.statut === 'brouillon' ? 'ri-pencil-line' : 'ri-exchange-line'}
                onClick={() => navigate(`/ventes/devis/modifier/${devis.id_proforma}`)}
                tooltip={devis.statut === 'brouillon' ? 'Modifier' : 'Changer le statut'}
            />
        )}
        
        {/* Bouton Convertir - visible uniquement pour les devis acceptés */}
        {devis.statut === 'accepte' && (
            <ActionButton
                variant="outline-success"
                icon="ri-file-list-3-line"
                onClick={() => navigate(`/ventes/factures/nouveau?devis=${devis.id_proforma}`)}
                tooltip="Convertir en facture"
            />
        )}
        
        {/* Bouton Envoyer - visible pour brouillon et envoyé (mais pas pour envoyé si déjà envoyé logiquement) */}
        {devis.statut === 'brouillon' && (
            <ActionButton
                variant="outline-warning"
                icon="ri-mail-send-line"
                onClick={() => navigate(`/ventes/devis/envoyer/${devis.id_proforma}`)}
                tooltip="Envoyer par email"
            />
        )}
    </div>
</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-file-copy-line empty-icon text-primary"></i>
                                                        <h5 className="mt-3">Aucun devis trouvé</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par créer votre premier devis"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>
                                                                Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/ventes/devis/nouveau')}
                                                            >
                                                                <i className="ri-add-line me-2"></i>
                                                                Créer un devis
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>
        </React.Fragment>
    );
}